import type {UseQueryOptions} from "@tanstack/react-query";

import {
  JOB_HANDLER_INTERVAL_DEFAULT_MS,
  JOB_HANDLER_INTERVAL_MAX_MS,
  JOB_HANDLER_INTERVAL_MIN_MS
} from "@/lib/jobs/job-constants";
import type {Job, JobHandler, PollResult} from "@/types/jobs";

export async function resolveJobPollResult(
  job: Job,
  handler: JobHandler,
  nowMs: number
): Promise<PollResult> {
  const timeoutMs = handler.timeoutMs ?? JOB_HANDLER_INTERVAL_MAX_MS;
  const startedAtMs = job.startedAt ?? job.createdAt;

  const elapsedMs = nowMs - startedAtMs;
  if (elapsedMs >= timeoutMs) {
    return {
      state: "cancelled",
      patch: { lastMessage: "Polling timed out" },
    };
  }

  try {
    return await handler.poll(job);
  } catch (error) {
    const message = error instanceof Error ? error.message : "Polling failed";
    return { state: "failed", patch: { lastMessage: message } };
  }
}

export function normalizePollIntervalMs(input?: number): number {

  if (typeof input !== "number" || Number.isNaN(input)) return JOB_HANDLER_INTERVAL_DEFAULT_MS;
  if (!Number.isFinite(input)) return JOB_HANDLER_INTERVAL_DEFAULT_MS;

  return Math.min(JOB_HANDLER_INTERVAL_MAX_MS, Math.max(JOB_HANDLER_INTERVAL_MIN_MS, input));
}

export function buildJobQueryOptions(params: {
  activeJobs: Job[];
  handlers: Map<string, JobHandler>;
}): UseQueryOptions<PollResult>[] {
  const { activeJobs, handlers } = params;
  const options: UseQueryOptions<PollResult>[] = [];

  for (const job of activeJobs) {
    const handler = handlers.get(job.type);
    if (!handler) continue;

    const pollIntervalMs = normalizePollIntervalMs(handler.intervalMs);

    options.push({
      queryKey: ["job", job.type, job.id],
      enabled: true,
      retry: false,
      throwOnError: false,
      refetchInterval: pollIntervalMs,
      queryFn: async () => {
        if (job.state === "success" || job.state === "failed" || job.state === "cancelled") {
          return { state: job.state };
        }
        return resolveJobPollResult(job, handler, Date.now());
      },
    });
  }
  return options;
}